import type { LRSDataPayload } from 'models/lrs';

type FlagsKey = readonly [string, ...string[]];

type OffersKeyParams = {
  sourceOfFunds?: string;
  loanProviderCategory?: string;
  loanProviderName?: string;
  recipientId?: string;
  country?: string;
  amount?: number;
};

export const queryKeys = {
  countries: {
    all: (locale: string) => ['countries', locale] as const,
  },
  currencies: {
    all: ['currencies'] as const,
  },
  instructions: {
    all: ['instructions'] as const,
  },
  links: {
    all: ['links'] as const,
  },
  geolocation: {
    all: ['geolocation'] as const,
  },
  flags: {
    by: (...flags: string[]): FlagsKey => ['flags', ...flags] as const,
  },
  studentFields: {
    by: (recipientId: string, payerRelationship: string) =>
      ['studentFields', recipientId, payerRelationship] as const,
  },
  capFee: {
    by: (currency: string) => ['capFee', currency] as const,
  },
  cnRegions: {
    all: ['cnRegions'] as const,
  },
  smsWhitelist: {
    all: ['smsWhitelist'] as const,
  },
  discounts: {
    config: (orderId: string) => ['discounts', 'config', orderId] as const,
  },
  userProfile: {
    account: ['userProfile', 'account'] as const,
    rewards: (currency: string) =>
      ['userProfile', 'rewards', currency] as const,
  },
  offers: {
    all: ['offers'] as const,
    india: (
      params: OffersKeyParams,
      recipientId?: string,
      country?: string,
      amount?: number,
    ) =>
      [
        'indiaOffers',
        params.sourceOfFunds,
        params.loanProviderCategory,
        params.loanProviderName,
        recipientId,
        country,
        amount,
      ] as const,
  },
  lrsPreview: {
    by: (
      paymentId: string | undefined,
      payload: Omit<LRSDataPayload, 'signatureImage'>,
    ) =>
      [
        'lrsPreview',
        paymentId,
        payload.bankAccountNumber,
        payload.studentID,
        payload.studentFullName,
        payload.transactions,
      ] as const,
  },
  userPayments: {
    all: ['userPayments'] as const,
    by: (page: number, perPage: number) =>
      ['userPayments', page, perPage] as const,
  },
  refunds: {
    all: ['refunds'] as const,
    by: (paymentId: string) => ['refunds', paymentId] as const,
  },
  kyc: {
    all: ['kyc'] as const,
    byId: (id: string) => ['kyc', id] as const,
    tokens: ['kyc', 'token'] as const,
    token: (reference: string | undefined) =>
      ['kyc', 'token', reference] as const,
  },
  zendesk: {
    sso: (returnTo?: string) => ['zendesk', 'sso', returnTo] as const,
  },
  smsNotifications: {
    by: (paymentId: string, url: string) =>
      ['smsNotifications', paymentId, url] as const,
  },
} as const;
